// ═══════════════════════════════════════════════════════════════════════════
//  src/components/YaEstasDentro.jsx
//  LO QUE VE UN ALUMNO RECIÉN VERIFICADO: lo que tiene, por dónde empieza y
//  qué puede pedir.
//
//  ── POR QUÉ ES UN COMPONENTE Y NO UNA PÁGINA ───────────────────────────────
//  Era la página `/bienvenida`. El enlace del correo abría una pestaña nueva
//  con ESA pantalla, distinta de la que el alumno tenía delante, y Rob lo dijo
//  claro el 11-09-2026:
//
//    «tenemos un registro para llegar y validar, pero se queda ahí, sin pasar
//     al siguiente nivel. Me lanza otra pestaña, y no parece lo mismo; la idea
//     es que valide en la misma pantalla y continuar… al dar clic se pone una
//     nueva pantalla, eso es un error porque no se siente lo mismo».
//
//  Tenía razón, y el síntoma se veía en la propia barra de pasos: REGISTRO →
//  VERIFICAR → APRENDER. El tercer paso no se alcanzaba nunca, porque el
//  recorrido se cortaba y empezaba otro en otra pantalla.
//
//  Ahora esto vive DENTRO de `/verify-email`: la misma pantalla, la misma
//  barra, el paso «Aprender» encendido y estas opciones debajo. Un solo sitio
//  de principio a fin.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import { enlaceWhatsApp } from '../contacto';
import { PRUEBA_TOKENS, duracionDeLaPrueba } from '../limites';
import '../pages/BienvenidaPage.css';

// Lo que se enseña si el servidor no contesta. No son precios inventados: son
// los tres planes de pago tal y como están hoy, y si el servidor responde se
// pisan con lo que él diga. Una pantalla de bienvenida en blanco es peor que
// una con los precios de ayer.
const PLANES_DE_RESPALDO = [
  { id: 42, name: 'Boost', price: 400, currency: 'MXN', token_limit: 2000, emoji: '⚡', color: '#60a5fa' },
  { id: 7, name: 'Jumpstart', price: 499, currency: 'MXN', token_limit: 3000, emoji: '🚀', color: '#a78bfa' },
  { id: 43, name: 'Elite', price: 700, currency: 'MXN', token_limit: 6000, emoji: '👑', color: '#fbbf24' },
];

function Whatsapp({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function YaEstasDentro({ correo = '' }) {
  const navigate = useNavigate();
  const [planes, setPlanes] = useState(PLANES_DE_RESPALDO);
  const [pedido, setPedido] = useState(null);   // el plan que acaba de pedir

  // ¿Ya tiene sesión en este navegador? Cambia a dónde lleva cada camino, nada
  // más. Sin sesión pasa por el login; con sesión, directo adentro.
  const haySesion = Boolean(localStorage.getItem('token'));

  useEffect(() => {
    let vivo = true;
    api.get('/api/public/plans')
      .then(({ data }) => {
        if (!vivo) return;
        // Los de pago, en orden de precio. El Freemium no va en esta lista:
        // tiene su propio sitio arriba, porque ya lo tiene.
        const dePago = (data?.subscriptions || [])
          .filter(p => Number(p.price) > 0)
          .sort((a, b) => Number(a.price) - Number(b.price));
        if (dePago.length) setPlanes(dePago);
      })
      .catch(() => { /* se quedan los de respaldo */ });
    return () => { vivo = false; };
  }, []);

  // El mensaje que le llega a gerencia. Lleva el correo porque es lo único con
  // lo que Rob puede encontrar la cuenta en el CRM para activarla: un nombre se
  // repite, un correo no.
  const pedirPlan = (plan) => {
    const texto = [
      `Hola, quiero activar el plan *${plan.name}*.`,
      '',
      correo ? `Mi correo de NSL: ${correo}` : 'Me acabo de registrar en NSL.',
      `Precio: $${Number(plan.price).toLocaleString('es-MX')} ${plan.currency || 'MXN'} al mes.`,
      '',
      '¿Me dices como hago el pago?',
    ].join('\n');
    window.open(enlaceWhatsApp(texto), '_blank', 'noopener');
    setPedido(plan);
  };

  // ── POR DÓNDE EMPIEZA ─────────────────────────────────────────────────
  //  Antes había un solo botón y llevaba a `/home`, el temario del curso. Dos
  //  problemas: el login lleva a `/entrenar`, así que el mismo alumno acababa
  //  en sitios distintos según por dónde entrara; y decidir por él no es lo que
  //  quiere Rob — «dale la oportunidad al usuario por dónde empezar, lo ideal
  //  primero su examen, al gym o al panel, que el usuario decida y decirle lo
  //  recomendado de primera instancia».
  //
  //  El examen va primero y marcado: sin nivel, el gimnasio no sabe qué
  //  ponerle. Pero es una recomendación, no un peaje: los otros dos caminos
  //  están al lado y se ven igual de abiertos.
  const CAMINOS = [
    {
      ruta: '/examenes',
      emoji: '🎯',
      titulo: 'Haz tu examen de nivel',
      pie: 'Unos minutos. Con tu nivel, todo lo demás se ajusta a ti.',
      recomendado: true,
    },
    {
      ruta: '/entrenar',
      emoji: '💪',
      titulo: 'Ir directo al gimnasio',
      pie: 'Si ya sabes por dónde vas, empieza a entrenar ahora.',
    },
    {
      ruta: '/dashboard',
      emoji: '📊',
      titulo: 'Ver mi panel',
      pie: 'Tu prueba, tu energía y todo lo que incluye.',
    },
  ];

  // Sin sesión no se puede ir a ninguna de las tres: se pasa por el login y se
  // le dice a dónde iba, para no perder la elección que acaba de hacer.
  const ir = (ruta) => navigate(haySesion ? ruta : '/login', {
    state: haySesion ? undefined : { destino: ruta },
  });

  return (
    <div className="bnv-dentro">
      {/* ── Lo que ya tiene, sin pedirle nada ─────────────────────────── */}
      <section className="bnv-tarjeta bnv-tarjeta-prueba">
        <div className="bnv-etiqueta">Ya es tuyo</div>
        <h2 className="bnv-tarjeta-titulo">
          <span className="bnv-emoji">🌱</span> Prueba gratuita
        </h2>
        <p className="bnv-tarjeta-sub">
          Tu cuenta ya tiene <strong>{PRUEBA_TOKENS} de Energía Spacy</strong> y{' '}
          <strong>{duracionDeLaPrueba()}</strong> para usarla. No hay que pagar
          nada ni poner una tarjeta.
        </p>
        <ul className="bnv-lista">
          <li>Entrenamiento completo de A1 a C2</li>
          <li>Spacy, tu tutor de IA</li>
          <li>Minijuegos y glosario</li>
          <li>Tu examen de nivel</li>
        </ul>
        <p className="bnv-nota bnv-nota-alta">
          Es de un solo uso por cuenta: cuando se acaben {duracionDeLaPrueba()},
          necesitarás un plan para seguir.
        </p>
      </section>

      {/* ── Por dónde empieza. Lo decide él ───────────────────────────── */}
      <section className="bnv-bloque-caminos">
        <h2 className="bnv-bloque-titulo">¿Por dónde quieres empezar?</h2>
        <p className="bnv-bloque-sub">
          Los tres están abiertos desde ya. Si no lo tienes claro, empieza por
          el examen: es lo que hace que el resto se ajuste a tu nivel.
        </p>

        <div className="bnv-caminos">
          {CAMINOS.map((c) => (
            <button
              key={c.ruta}
              className={`bnv-camino ${c.recomendado ? 'bnv-camino-top' : ''}`}
              // El destino, declarado. Sin sesión los tres pasan por el
              // login, así que mirar a dónde salta el navegador no distingue
              // uno de otro: la auditoría comprobaba el destino y en realidad
              // no medía nada. Aquí está el dato que de verdad decide.
              data-ruta={c.ruta}
              onClick={() => ir(c.ruta)}
            >
              {c.recomendado && (
                <span className="bnv-camino-sello">Recomendado</span>
              )}
              <span className="bnv-camino-emoji">{c.emoji}</span>
              <span className="bnv-camino-texto">
                <span className="bnv-camino-titulo">{c.titulo}</span>
                <span className="bnv-camino-pie">{c.pie}</span>
              </span>
              <span className="bnv-camino-flecha">›</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Y lo que puede pedir ──────────────────────────────────────── */}
      <section className="bnv-bloque-planes">
        <h2 className="bnv-bloque-titulo">¿Prefieres ir en serio desde hoy?</h2>
        <p className="bnv-bloque-sub">
          Elige tu plan y gerencia te lo activa. Mientras tanto entras con tu
          prueba: no esperas nada.
        </p>

        <div className="bnv-rejilla">
          {planes.map((plan) => {
            const esElPedido = pedido?.id === plan.id;
            return (
              <article
                key={plan.id}
                className={`bnv-plan ${esElPedido ? 'bnv-plan-pedido' : ''}`}
                style={{ '--plan-color': plan.color || '#ffff00' }}
              >
                <div className="bnv-plan-cabeza">
                  <span className="bnv-plan-emoji">{plan.emoji || '✦'}</span>
                  <h3 className="bnv-plan-nombre">{plan.name}</h3>
                </div>
                <div className="bnv-plan-precio">
                  ${Number(plan.price).toLocaleString('es-MX')}
                  <span className="bnv-plan-moneda">{plan.currency || 'MXN'}/mes</span>
                </div>
                <div className="bnv-plan-tokens">
                  ⚡ {Number(plan.token_limit).toLocaleString('es-MX')} de Energía al mes
                </div>
                <button
                  className="bnv-boton bnv-boton-plan"
                  onClick={() => pedirPlan(plan)}
                >
                  <Whatsapp size={18} />
                  {esElPedido ? 'Volver a escribir' : 'Pedir este plan'}
                </button>
              </article>
            );
          })}
        </div>

        {/* Se abrió WhatsApp: se le dice qué pasa ahora y se le quita el
            miedo a quedarse fuera mientras espera. */}
        {pedido && (
          <div className="bnv-pedido-aviso" role="status">
            <strong>Se abrió WhatsApp con tu solicitud de {pedido.name}.</strong>
            <span>
              Si no se abrió, escríbenos tú:{' '}
              <a
                href={enlaceWhatsApp(`Hola, quiero activar el plan ${pedido.name}.` +
                  (correo ? ` Mi correo de NSL: ${correo}` : ''))}
                target="_blank"
                rel="noopener noreferrer"
              >
                abrir WhatsApp
              </a>.
            </span>
            <span>
              Mientras gerencia lo activa, entra con tu prueba — no tienes que
              esperar.
            </span>
            <button
              className="bnv-boton bnv-boton-principal"
              onClick={() => ir(CAMINOS[0].ruta)}
            >
              Entrar mientras tanto →
            </button>
          </div>
        )}

        <Link className="bnv-ver-todo" to="/plans">
          Ver todos los planes y lo que incluye cada uno
        </Link>
      </section>
    </div>
  );
}
